
import { useMemo, useCallback, useEffect, useState } from "react";
import { ensureCsrfToken } from "../../../api";
import Swal from "sweetalert2";
import "./css/enterprisesetting.css";

const SAC_API_URL = "/api/hsn-sac-list/";

const SAC_COLUMNS = [
  { key: "saccode", label: "SAC Code" },
  { key: "service_code", label: "Service Code" },
  { key: "sacDescription", label: "Description" },
  { key: "block_input", label: "BC / IC" },
  { key: "rcm_fc", label: "RCM / FC" },
  { key: "section", label: "Section" },
  { key: "tds_section_2025", label: "TDS Section 2025" },
];

/* =========================================================
   SAC EDIT MODAL
========================================================= */

function SACEditModal({
  sac,
  options,
  saving,
  onClose,
  onSaved,
}) {
  const [formData, setFormData] = useState({
    taxrate: sac.taxrate || "",
    block_input: sac.block_input || "",
    rcm_fc: sac.rcm_fc || "",
    section: sac.section || "",
  });

  const [savingLocal, setSavingLocal] = useState(false);

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setSavingLocal(true);

      const csrfToken = await ensureCsrfToken();

      const response = await fetch(
        "/api/hsn-sac-list/sac/save/",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            default_id: sac.id,
            taxrate: formData.taxrate,
            block_input: formData.block_input,
            rcm_fc: formData.rcm_fc,
            section: formData.section,
          }),
        },
      );

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(
          payload.message ||
            "Unable to save SAC master data.",
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text:
          payload.message ||
          "SAC master data saved successfully.",
        confirmButtonText: "OK",
      });

      await onSaved();
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message ||
          "Unable to save SAC master data.",
        confirmButtonText: "OK",
      });
    } finally {
      setSavingLocal(false);
    }
  }

  return (
    <div className="hsn-sac-modal__backdrop">
      <div className="hsn-sac-modal">
        <div className="hsn-sac-modal__header">
          <div>
            <p className="eyebrow">SAC Master</p>

            <h2>
              Edit SAC — {sac.saccode || "—"}
            </h2>
          </div>

          <button
            type="button"
            className="hsn-sac-modal__close"
            onClick={onClose}
            disabled={savingLocal}
          >
            ×
          </button>
        </div>

        <form
          className="hsn-sac-modal__form"
          onSubmit={handleSubmit}
        >
          <SACReadOnlyField
            label="SAC Code"
            value={sac.saccode}
          />

          <SACReadOnlyField
            label="Chapter"
            value={sac.chapter}
          />

          <SACReadOnlyField
            label="Heading"
            value={sac.heading}
          />

          <SACReadOnlyField
            label="Service Code"
            value={sac.service_code}
          />

          <SACReadOnlyField
            label="Description"
            value={sac.sacDescription}
            multiline
          />

          <SACSelectField
            label="Tax Rate"
            name="taxrate"
            value={formData.taxrate}
            options={options.taxrate}
            onChange={handleChange}
          />

          <SACReadOnlyField
            label="PBC"
            value={sac.pbc}
          />

          <SACSelectField
            label="Block Input"
            name="block_input"
            value={formData.block_input}
            options={options.block_input}
            onChange={handleChange}
          />

          <SACReadOnlyField
            label="Deciding Factor"
            value={sac.deciding_factor}
            multiline
          />

          <SACReadOnlyField
            label="Effective Date"
            value={sac.effective_date}
          />

          <SACReadOnlyField
            label="PRCM"
            value={sac.prcm}
          />

          <SACSelectField
            label="RCM / FC"
            name="rcm_fc"
            value={formData.rcm_fc}
            options={options.rcm_fc}
            onChange={handleChange}
          />

          <SACReadOnlyField
            label="FC RCM Effective Date"
            value={sac.fc_rcm_effective_date}
          />

          <SACReadOnlyField
            label="FC RCM Deciding Factor"
            value={sac.fc_rcm_deciding_factor}
            multiline
          />

          <SACReadOnlyField
            label="PRC"
            value={sac.prc}
          />

          <SACSelectField
            label="Section"
            name="section"
            value={formData.section}
            options={options.section}
            onChange={handleChange}
          />

          <SACReadOnlyField
            label="TDS Section 2025"
            value={sac.tds_section_2025}
          />

          <SACReadOnlyField
            label="TDS Effective Date"
            value={sac.tds_effective_date}
          />

          <SACReadOnlyField
            label="TDS Deciding Factor"
            value={sac.tds_deciding_factor}
            multiline
          />

          <div className="hsn-sac-modal__actions">
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
              disabled={savingLocal}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="primary-button"
              disabled={savingLocal || saving}
            >
              {savingLocal ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SACReadOnlyField({
  label,
  value,
  multiline = false,
}) {
  return (
    <div className="hsn-sac-modal__field">
      <label>{label}</label>

      {multiline ? (
        <textarea
          value={value || ""}
          readOnly
          rows={3}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          readOnly
        />
      )}
    </div>
  );
}

function SACSelectField({
  label,
  name,
  value,
  options = [],
  onChange,
}) {
  return (
    <div className="hsn-sac-modal__field">
      <label htmlFor={`sac-edit-${name}`}>
        {label}
      </label>

      <select
        id={`sac-edit-${name}`}
        name={name}
        value={value}
        onChange={onChange}
      >
        <option value="">
          Select {label}
        </option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

/* =========================================================
   HSN EDIT MODAL
========================================================= */

function HSNEditModal({
  hsn,
  options,
  saving,
  onClose,
  onSaved,
}) {
  const [formData, setFormData] = useState({
    tax_rate: hsn.tax_rate || "",
    block_input: hsn.block_input || "",
  });

  const [savingLocal, setSavingLocal] = useState(false);

  const handleChange = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSavingLocal(true);

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch(
        "/api/hsn-sac-list/hsn/save/",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            hsncode: hsn.hsncode,
            tax_rate: formData.tax_rate,
            block_input: formData.block_input,
          }),
        },
      );

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(
          payload.message ||
            "Unable to save HSN master data.",
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text:
          payload.message ||
          "HSN master data saved successfully.",
        confirmButtonText: "OK",
      });

      await onSaved();
    } catch (requestError) {
      console.error(
        "Failed to save HSN master:",
        requestError,
      );

      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          requestError.message ||
          "Unable to save HSN master data.",
        confirmButtonText: "OK",
      });
    } finally {
      setSavingLocal(false);
    }
  };

  return (
    <div className="hsn-sac-modal__backdrop">
      <div className="hsn-sac-modal">
        <div className="hsn-sac-modal__header">
          <div>
            <p className="eyebrow">HSN Master</p>

            <h3>
              Edit HSN {displayValue(hsn.hsncode)}
            </h3>
          </div>

          <button
            type="button"
            className="hsn-sac-modal__close"
            onClick={onClose}
            disabled={savingLocal}
          >
            ×
          </button>
        </div>

        <form
          className="hsn-sac-modal__form"
          onSubmit={handleSubmit}
        >
          <HSNReadOnlyField
            label="HSN Code"
            value={hsn.hsncode}
          />

          <HSNReadOnlyField
            label="Chapter"
            value={hsn.chapter}
          />

          <HSNReadOnlyField
            label="Heading"
            value={hsn.heading}
          />

          <HSNReadOnlyField
            label="Sub Heading"
            value={hsn.sub_heading}
          />

          <HSNReadOnlyField
            label="Tariff"
            value={hsn.tarriff}
          />

          <HSNReadOnlyField
            label="Description"
            value={hsn.hsnDescription}
            fullWidth
          />

          <HSNSelectField
            label="Tax Rate"
            value={formData.tax_rate}
            options={options.tax_rate}
            onChange={(value) =>
              handleChange("tax_rate", value)
            }
          />

          <HSNReadOnlyField
            label="PBC"
            value={hsn.pbc}
          />

          <HSNSelectField
            label="Block Input"
            value={formData.block_input}
            options={options.block_input}
            onChange={(value) =>
              handleChange("block_input", value)
            }
          />

          <HSNReadOnlyField
            label="Effective Date"
            value={hsn.effective_date}
          />

          <HSNReadOnlyField
            label="Deciding Factor"
            value={hsn.deciding_factor}
            fullWidth
          />

          <div className="hsn-sac-modal__actions">
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
              disabled={savingLocal}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="primary-button"
              disabled={savingLocal || saving}
            >
              {savingLocal ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HSNReadOnlyField({
  label,
  value,
  fullWidth = false,
}) {
  return (
    <div
      className={`hsn-sac-modal__field ${
        fullWidth
          ? "hsn-sac-modal__field--full"
          : ""
      }`}
    >
      <label>{label}</label>

      <div className="hsn-sac-modal__readonly">
        {displayValue(value)}
      </div>
    </div>
  );
}

function HSNSelectField({
  label,
  value,
  options = [],
  onChange,
}) {
  return (
    <div className="hsn-sac-modal__field">
      <label>{label}</label>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
      >
        <option value="">
          Select {label}
        </option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

/* =========================================================
   PAGINATION
========================================================= */

function MasterPagination({
  pagination,
  onPageChange,
}) {
  if (!pagination || pagination.total_pages <= 1) {
    return null;
  }

  const {
    page,
    total,
    total_pages,
  } = pagination;

  return (
    <div className="hsn-sac-pagination">
      <div className="hsn-sac-pagination__info">
        Showing page {page} of {total_pages}
        {" "}
        ({total} records)
      </div>

      <div className="hsn-sac-pagination__controls">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>

        <span className="hsn-sac-pagination__current">
          {page}
        </span>

        <button
          type="button"
          disabled={page >= total_pages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function HSNSACMasterView() {
  const [activeTab, setActiveTab] = useState("sac");

  const [sacData, setSacData] = useState([]);
  const [hsnData, setHsnData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ---------------- SAC FILTERS ---------------- */

  const [sacSearch, setSacSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [rcmFilter, setRcmFilter] = useState("");
  const [blockInputFilter, setBlockInputFilter] = useState("");

  /* ---------------- HSN FILTERS ---------------- */

  const [hsnSearch, setHsnSearch] = useState("");
  const [hsnTaxrateFilter, setHsnTaxrateFilter] =
    useState("");
  const [hsnBlockInputFilter, setHsnBlockInputFilter] =
    useState("");

  /* ---------------- OPTIONS ---------------- */

  const [sacOptions, setSacOptions] = useState({
    taxrate: [],
    block_input: [],
    rcm_fc: [],
    section: [],
  });

  const [hsnOptions, setHsnOptions] = useState({
    tax_rate: [],
    block_input: [],
  });

  /* ---------------- EDIT STATE ---------------- */

  const [editingSac, setEditingSac] = useState(null);
  const [savingSac, setSavingSac] = useState(false);

  const [editingHsn, setEditingHsn] = useState(null);
  const [savingHsn, setSavingHsn] = useState(false);

  /* ---------------- PAGINATION ---------------- */

  const [sacPagination, setSacPagination] =
    useState({
      page: 1,
      page_size: 50,
      total: 0,
      total_pages: 0,
    });

  const [hsnPagination, setHsnPagination] =
    useState({
      page: 1,
      page_size: 50,
      total: 0,
      total_pages: 0,
    });

  /* =========================================================
     LOAD DATA
  ========================================================= */

  const loadSACData = useCallback(
    async ({
      type = activeTab,
      page = 1,
      search = "",
      section = "",
      rcm_fc = "",
      block_input = "",
      tax_rate = "",
    } = {}) => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();

        params.set("type", type);
        params.set("page", String(page));
        params.set("page_size", "50");

        if (search?.trim()) {
          params.set("search", search.trim());
        }

        if (type === "sac") {
          if (section) {
            params.set("section", section);
          }

          if (rcm_fc) {
            params.set("rcm_fc", rcm_fc);
          }

          if (block_input) {
            params.set("block_input", block_input);
          }
        } else {
          if (tax_rate) {
            params.set("tax_rate", tax_rate);
          }

          if (block_input) {
            params.set("block_input", block_input);
          }
        }

        const response = await fetch(
          `${SAC_API_URL}?${params.toString()}`,
          {
            credentials: "include",
            headers: {
              Accept: "application/json",
            },
          },
        );

        const payload = await response.json();

        if (
          !response.ok ||
          payload.status !== "success"
        ) {
          throw new Error(
            payload.message ||
              "Unable to load HSN/SAC master data.",
          );
        }

        if (type === "sac") {
          setSacData(payload.data || []);

          setSacPagination(
            payload.pagination || {
              page: 1,
              page_size: 50,
              total: 0,
              total_pages: 0,
            },
          );

          setSacOptions(
            payload.options || {
              taxrate: [],
              block_input: [],
              rcm_fc: [],
              section: [],
            },
          );
        } else {
          setHsnData(payload.data || []);

          setHsnPagination(
            payload.pagination || {
              page: 1,
              page_size: 50,
              total: 0,
              total_pages: 0,
            },
          );

          setHsnOptions(
            payload.options || {
              tax_rate: [],
              block_input: [],
            },
          );
        }
      } catch (requestError) {
        setError(
          requestError.message ||
            "Unable to load HSN/SAC master data.",
        );

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            requestError.message ||
            "Unable to load HSN/SAC master data.",
          confirmButtonText: "OK",
        });
      } finally {
        setLoading(false);
      }
    },
    [activeTab],
  );

  useEffect(() => {
    loadSACData();
  }, [loadSACData]);

  /* =========================================================
     FILTER OPTIONS
     
     These come from backend now.
     ========================================================= */

  const sectionOptions = useMemo(
    () => sacOptions.section || [],
    [sacOptions.section],
  );

  const rcmOptions = useMemo(
    () => sacOptions.rcm_fc || [],
    [sacOptions.rcm_fc],
  );

  const blockInputOptions = useMemo(
    () => sacOptions.block_input || [],
    [sacOptions.block_input],
  );

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const hasSacFilters =
    Boolean(sacSearch) ||
    Boolean(sectionFilter) ||
    Boolean(rcmFilter) ||
    Boolean(blockInputFilter);

  const hasHsnFilters =
    Boolean(hsnSearch) ||
    Boolean(hsnTaxrateFilter) ||
    Boolean(hsnBlockInputFilter);

  /* =========================================================
     CLEAR FILTERS
  ========================================================= */

  const clearSacFilters = () => {
    setSacSearch("");
    setSectionFilter("");
    setRcmFilter("");
    setBlockInputFilter("");

    loadSACData({
      type: "sac",
      page: 1,
    });
  };

  const clearHsnFilters = () => {
    setHsnSearch("");
    setHsnTaxrateFilter("");
    setHsnBlockInputFilter("");

    loadSACData({
      type: "hsn",
      page: 1,
    });
  };

  /* =========================================================
     EDIT SAC
  ========================================================= */

  const openSacEdit = async (row) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        type: "sac",
        code: row.saccode,
      });

      const response = await fetch(
        `${SAC_API_URL}?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      const payload = await response.json();

      if (
        !response.ok ||
        payload.status !== "success"
      ) {
        throw new Error(
          payload.message ||
            "Unable to load SAC record.",
        );
      }

      setEditingSac(payload.data);
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          requestError.message ||
          "Unable to load SAC record.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     EDIT HSN
  ========================================================= */

  const openHsnEdit = async (row) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        type: "hsn",
        code: row.hsncode,
      });

      const response = await fetch(
        `${SAC_API_URL}?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      const payload = await response.json();

      if (
        !response.ok ||
        payload.status !== "success"
      ) {
        throw new Error(
          payload.message ||
            "Unable to load HSN record.",
        );
      }

      setEditingHsn(payload.data);
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          requestError.message ||
          "Unable to load HSN record.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <section className="config-view">
      <div className="workspace-panel upload-view__hero">
        <div>
          <p className="eyebrow">Master Data</p>

          <h2>HSN / SAC Master</h2>

          <p className="config-view__intro">
            Manage HSN and SAC master data used for tax
            mapping, validation and invoice processing.
          </p>
        </div>

        <div className="upload-view__summary">
          <span className="upload-view__summary-count">
            {loading
              ? "..."
              : activeTab === "sac"
                ? sacPagination.total
                : hsnPagination.total}
          </span>

          <span className="upload-view__summary-label">
            {activeTab === "sac"
              ? "SAC records"
              : "HSN records"}
          </span>
        </div>
      </div>

      {error ? (
        <div className="config-banner config-banner--error">
          {error}
        </div>
      ) : null}

      <div className="workspace-panel hsn-sac-panel">
        {/* =================================================
            TABS
        ================================================= */}

        <div className="hsn-sac-panel__tabs">
          <button
            type="button"
            className={`hsn-sac-panel__tab ${
              activeTab === "sac"
                ? "hsn-sac-panel__tab--active"
                : ""
            }`}
            onClick={() => {
              setActiveTab("sac");

              loadSACData({
                type: "sac",
                page: 1,
                search: sacSearch,
                section: sectionFilter,
                rcm_fc: rcmFilter,
                block_input: blockInputFilter,
              });
            }}
          >
            SAC
          </button>

          <button
            type="button"
            className={`hsn-sac-panel__tab ${
              activeTab === "hsn"
                ? "hsn-sac-panel__tab--active"
                : ""
            }`}
            onClick={() => {
              setActiveTab("hsn");

              loadSACData({
                type: "hsn",
                page: 1,
                search: hsnSearch,
                tax_rate: hsnTaxrateFilter,
                block_input: hsnBlockInputFilter,
              });
            }}
          >
            HSN
          </button>
        </div>

        {/* =================================================
            SAC
        ================================================= */}

        {activeTab === "sac" ? (
          <div className="hsn-sac-panel__content">

            <div className="hsn-sac-filters">

              {/* SEARCH */}

              <div className="hsn-sac-filter hsn-sac-filter--search">
                <label htmlFor="sac-search">
                  SAC Code
                </label>

                <input
                  id="sac-search"
                  type="text"
                  value={sacSearch}
                  placeholder="Search SAC code..."
                  onChange={(event) =>
                    setSacSearch(event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      loadSACData({
                        type: "sac",
                        page: 1,
                        search: sacSearch,
                        section: sectionFilter,
                        rcm_fc: rcmFilter,
                        block_input:
                          blockInputFilter,
                      });
                    }
                  }}
                />
              </div>

              {/* SECTION */}

              <div className="hsn-sac-filter">
                <label htmlFor="sac-section">
                  Section
                </label>

                <select
                  id="sac-section"
                  value={sectionFilter}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setSectionFilter(value);

                    loadSACData({
                      type: "sac",
                      page: 1,
                      search: sacSearch,
                      section: value,
                      rcm_fc: rcmFilter,
                      block_input:
                        blockInputFilter,
                    });
                  }}
                >
                  <option value="">
                    All Sections
                  </option>

                  {sectionOptions.map((value) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              {/* RCM / FC */}

              <div className="hsn-sac-filter">
                <label htmlFor="sac-rcm">
                  RCM / FC
                </label>

                <select
                  id="sac-rcm"
                  value={rcmFilter}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setRcmFilter(value);

                    loadSACData({
                      type: "sac",
                      page: 1,
                      search: sacSearch,
                      section: sectionFilter,
                      rcm_fc: value,
                      block_input:
                        blockInputFilter,
                    });
                  }}
                >
                  <option value="">
                    All RCM / FC
                  </option>

                  {rcmOptions.map((value) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              {/* BLOCK INPUT */}

              <div className="hsn-sac-filter">
                <label htmlFor="sac-block-input">
                  Block Input
                </label>

                <select
                  id="sac-block-input"
                  value={blockInputFilter}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setBlockInputFilter(value);

                    loadSACData({
                      type: "sac",
                      page: 1,
                      search: sacSearch,
                      section: sectionFilter,
                      rcm_fc: rcmFilter,
                      block_input: value,
                    });
                  }}
                >
                  <option value="">
                    All Block Input
                  </option>

                  {blockInputOptions.map((value) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              {hasSacFilters ? (
                <button
                  type="button"
                  className="secondary-button hsn-sac-filter__clear"
                  onClick={clearSacFilters}
                >
                  Clear Filters
                </button>
              ) : null}

            </div>

            {/* SAC TABLE */}

            <div className="hsn-sac-table-wrap">
              {loading ? (
                <div className="hsn-sac-table__state">
                  Loading SAC master data...
                </div>
              ) : sacData.length === 0 ? (
                <div className="hsn-sac-table__state">
                  No SAC records found for the
                  selected filters.
                </div>
              ) : (
                <table className="hsn-sac-table">
                  <thead>
                    <tr>
                      {SAC_COLUMNS.map((column) => (
                        <th key={column.key}>
                          {column.label}
                        </th>
                      ))}

                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {sacData.map((row, index) => (
                      <tr
                        key={
                          row.id ??
                          `${row.saccode}-${index}`
                        }
                      >
                        <td>
                          <strong>
                            {displayValue(
                              row.saccode,
                            )}
                          </strong>
                        </td>

                        <td>
                          {displayValue(
                            row.service_code,
                          )}
                        </td>

                        <td className="hsn-sac-table__description">
                          {displayValue(
                            row.sacDescription,
                          )}
                        </td>

                        <td>
                          <span
                            className="hsn-sac-table__tooltip-wrapper"
                            title={
                              row.deciding_factor ||
                              "No deciding factor available"
                            }
                          >
                            <span className="pill">
                              {displayValue(
                                row.block_input,
                              )}
                            </span>
                          </span>
                        </td>

                        <td>
                          <span
                            className="hsn-sac-table__tooltip-wrapper"
                            title={
                              row.fc_rcm_deciding_factor ||
                              "No deciding factor available"
                            }
                          >
                            <span className="pill">
                              {displayValue(
                                row.rcm_fc,
                              )}
                            </span>
                          </span>
                        </td>

                        <td>
                          {displayValue(
                            row.section,
                          )}
                        </td>

                        <td>
                          {displayValue(
                            row.tds_section_2025,
                          )}
                        </td>

                        <td>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() =>
                              openSacEdit(row)
                            }
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <MasterPagination
              pagination={sacPagination}
              onPageChange={(page) => {
                loadSACData({
                  type: "sac",
                  page,
                  search: sacSearch,
                  section: sectionFilter,
                  rcm_fc: rcmFilter,
                  block_input:
                    blockInputFilter,
                });
              }}
            />

          </div>
        ) : (

          /* =================================================
             HSN
          ================================================= */

          <div className="hsn-sac-panel__content">

            <div className="hsn-sac-filters">

              {/* SEARCH */}

              <div className="hsn-sac-filter hsn-sac-filter--search">
                <label htmlFor="hsn-search">
                  HSN Code
                </label>

                <input
                  id="hsn-search"
                  type="text"
                  value={hsnSearch}
                  placeholder="Search HSN code..."
                  onChange={(event) =>
                    setHsnSearch(event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      loadSACData({
                        type: "hsn",
                        page: 1,
                        search: hsnSearch,
                        tax_rate:
                          hsnTaxrateFilter,
                        block_input:
                          hsnBlockInputFilter,
                      });
                    }
                  }}
                />
              </div>

              {/* TAX RATE */}

              <div className="hsn-sac-filter">
                <label htmlFor="hsn-taxrate">
                  Tax Rate
                </label>

                <select
                  id="hsn-taxrate"
                  value={hsnTaxrateFilter}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setHsnTaxrateFilter(value);

                    loadSACData({
                      type: "hsn",
                      page: 1,
                      search: hsnSearch,
                      tax_rate: value,
                      block_input:
                        hsnBlockInputFilter,
                    });
                  }}
                >
                  <option value="">
                    All Tax Rates
                  </option>

                  {hsnOptions.tax_rate.map(
                    (value) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </div>

              {/* BLOCK INPUT */}

              <div className="hsn-sac-filter">
                <label htmlFor="hsn-block-input">
                  Block Input
                </label>

                <select
                  id="hsn-block-input"
                  value={hsnBlockInputFilter}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setHsnBlockInputFilter(value);

                    loadSACData({
                      type: "hsn",
                      page: 1,
                      search: hsnSearch,
                      tax_rate:
                        hsnTaxrateFilter,
                      block_input: value,
                    });
                  }}
                >
                  <option value="">
                    All Block Input
                  </option>

                  {hsnOptions.block_input.map(
                    (value) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </div>

              {hasHsnFilters ? (
                <button
                  type="button"
                  className="secondary-button hsn-sac-filter__clear"
                  onClick={clearHsnFilters}
                >
                  Clear Filters
                </button>
              ) : null}

            </div>

            {/* HSN TABLE */}

            <div className="hsn-sac-table-wrap">
              {loading ? (
                <div className="hsn-sac-table__state">
                  Loading HSN master data...
                </div>
              ) : hsnData.length === 0 ? (
                <div className="hsn-sac-table__state">
                  No HSN records found for the
                  selected filters.
                </div>
              ) : (
                <table className="hsn-sac-table">
                  <thead>
                    <tr>
                      <th>HSN Code</th>
                      <th>Description</th>
                      <th>Tax Rate</th>
                      <th>Block Input</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {hsnData.map((row, index) => (
                      <tr
                        key={
                          row.id ??
                          `${row.hsncode}-${index}`
                        }
                      >
                        <td>
                          <strong>
                            {displayValue(
                              row.hsncode,
                            )}
                          </strong>
                        </td>

                        <td className="hsn-sac-table__description">
                          {displayValue(
                            row.hsnDescription ??
                              row.description,
                          )}
                        </td>

                        <td>
                          <span className="pill">
                            {displayValue(
                              row.tax_rate,
                            )}
                          </span>
                        </td>

                        <td>
                          <span
                            className="hsn-sac-table__tooltip-wrapper"
                            title={
                              row.deciding_factor ||
                              "No deciding factor available"
                            }
                          >
                            <span className="pill">
                              {displayValue(
                                row.block_input,
                              )}
                            </span>
                          </span>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() =>
                              openHsnEdit(row)
                            }
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <MasterPagination
              pagination={hsnPagination}
              onPageChange={(page) => {
                loadSACData({
                  type: "hsn",
                  page,
                  search: hsnSearch,
                  tax_rate:
                    hsnTaxrateFilter,
                  block_input:
                    hsnBlockInputFilter,
                });
              }}
            />

          </div>
        )}
      </div>

      {/* =====================================================
          EDIT MODALS
      ===================================================== */}

      {editingSac ? (
        <SACEditModal
          sac={editingSac}
          options={sacOptions}
          saving={savingSac}
          onClose={() => setEditingSac(null)}
          onSaved={async () => {
            setEditingSac(null);

            await loadSACData({
              type: "sac",
              page: sacPagination.page,
              search: sacSearch,
              section: sectionFilter,
              rcm_fc: rcmFilter,
              block_input:
                blockInputFilter,
            });
          }}
        />
      ) : null}

      {editingHsn ? (
        <HSNEditModal
          hsn={editingHsn}
          options={hsnOptions}
          saving={savingHsn}
          onClose={() => setEditingHsn(null)}
          onSaved={async () => {
            setEditingHsn(null);

            await loadSACData({
              type: "hsn",
              page: hsnPagination.page,
              search: hsnSearch,
              tax_rate: hsnTaxrateFilter,
              block_input:
                hsnBlockInputFilter,
            });
          }}
        />
      ) : null}
    </section>
  );
}

/* =========================================================
   HELPERS
========================================================= */

function normalizeValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
}

function displayValue(value) {
  const normalized = normalizeValue(value);

  return normalized || "—";
}

